import type { ServerPaginationState } from "@/components/data-list";
import { useCallback, useEffect, useMemo, useState } from "react";

const getResponsivePageSize = (width: number): number => {
  if (width >= 1280) return 12;
  if (width >= 1024) return 9;
  if (width >= 640) return 6;
  return 4;
};

const getResponsivePageSizeOptions = (width: number): number[] => {
  const base = getResponsivePageSize(width);
  return [base, base * 2, base * 4, base * 8];
};

export function useResponsivePageSize(totalItems: number) {
  const [paginationState, setPaginationState] = useState<ServerPaginationState>(() => ({
    page: 1,
    pageSize: getResponsivePageSize(window.innerWidth),
  }));
  const [pageSizeOptions, setPageSizeOptions] = useState<number[]>(() =>
    getResponsivePageSizeOptions(window.innerWidth),
  );

  useEffect(() => {
    const handleResize = () => {
      setPageSizeOptions(getResponsivePageSizeOptions(window.innerWidth));
      const pageSize = getResponsivePageSize(window.innerWidth);
      setPaginationState((previousState) => {
        if (previousState.pageSize === pageSize) {
          return previousState;
        }

        const firstVisibleIndex = Math.max(0, (previousState.page - 1) * previousState.pageSize);
        const maxPage = Math.max(1, Math.ceil(totalItems / pageSize));
        const nextPage = Math.min(Math.floor(firstVisibleIndex / pageSize) + 1, maxPage);

        return {
          page: nextPage,
          pageSize,
        };
      });
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [totalItems]);

  const effectivePaginationState = useMemo(() => {
    const maxPage = Math.max(1, Math.ceil(totalItems / paginationState.pageSize));

    if (paginationState.page <= maxPage) {
      return paginationState;
    }

    return {
      ...paginationState,
      page: maxPage,
    };
  }, [totalItems, paginationState]);

  const handlePaginationChange = useCallback((state: ServerPaginationState) => {
    setPaginationState(state);
  }, []);

  return { paginationState: effectivePaginationState, pageSizeOptions, handlePaginationChange };
}
